// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import { useTranslation } from '../i18n/i18n';
import { AuthContext } from '../context/AuthContext';
import { useContext } from 'react';
import { X } from 'lucide-react';

export default function ConfirmationModal({ title, subTitle, cancelText, confirmText, handleCancel, handleConfirm }) {
  const { currentUser } = useContext(AuthContext);
  const { t } = useTranslation(currentUser?.info?.languageCode);

  return (
    <div className='fixed inset-0 z-[1000] bg-black/70 backdrop-blur-sm flex justify-center items-center animate-fadein'>
      <div className='rounded-3xl dark:border dark:border-[#3C3E53] bg-[#FFFFFFF2] dark:bg-[#2E3440] shadow-[58px_41px_42px_0_rgba(0,0,0,0.07),_6px_5px_17px_0_rgba(0,0,0,0.13)] dark:shadow-[0_10px_15px_-3px_rgba(0,0,0,0.10)] p-6 flex flex-col gap-2 max-w-[25rem] w-full relative animate-popup'>
        <X
          className='size-5 text-[#7E7E7E] hover:text-[#4B4B4B] dark:text-[#90A1B9] dark:hover:text-[#CAD5E2] absolute top-5 right-5 cursor-pointer transition-all duration-300'
          onClick={() => handleCancel?.()}
        />
        {title && (
          <p className='text-xl font-semibold text-[#0D0D0D] dark:text-[#FAFAFA] text-center px-5 whitespace-pre-line'>
            {title}
          </p>
        )}
        {subTitle && (
          <p className='text-base font-medium text-[#4B4B4B] dark:text-[#CAD5E2] text-center whitespace-pre-line'>
            {subTitle}
          </p>
        )}
        {(handleCancel || handleConfirm) && (
          <div className='flex items-center justify-end gap-3 mt-6'>
            {handleCancel && (
              <button
                className='h-12 min-w-[10.625rem] rounded-lg flex items-center justify-center border border-[#E1E1E1] dark:border-[#3C3E53] bg-[#FAFAFA] hover:bg-[#F5F5F5] active:bg-[#E1E1E1] dark:bg-[#0C121D] dark:hover:bg-[#1A202E] dark:active:bg-[#0C121D] py-2 px-4 text-lg leading-[120%] font-medium dark:text-[#CAD5E2] box-border transition-all duration-300'
                onClick={() => handleCancel?.()}
              >
                {cancelText || t('common_cancel')}
              </button>
            )}
            {handleConfirm && (
              <button
                className='h-12 min-w-[10.625rem] rounded-lg flex items-center justify-center bg-[#DA1E28] hover:bg-[#C41A24] active:bg-[#DA1E28] dark:bg-[#E04B4B] dark:hover:bg-[#D32F2F] dark:active:bg-[#E04B4B] py-2 px-4 text-lg leading-[120%] font-medium text-[#FFFFFF] dark:text-[#FAFAFA] box-border transition-all duration-300'
                onClick={() => {
                  handleConfirm?.();
                  handleCancel?.();
                }}
              >
                {confirmText || t('common_confirm')}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
